import { useRef, useState } from 'react';
import {
  Monitor,
  Check,
  X,
  RefreshCw,
  Store,
  Shield,
  Building2,
  Save,
  Eye,
  Trash2,
  Upload,
  Loader2,
  Tv,
} from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import {
  usePosDevices,
  usePosTenantConfig,
  usePosRolePermissions,
  useUpdatePosTenantConfig,
  useUpdatePosRolePermissions,
  useProductsPosConfig,
  useUpdateProductsPosConfig,
  useDeletePosRegister,
  DEFAULT_COST_VISIBLE_ROLES,
} from '@/hooks/use-pos-config';
import type { PosRolePermission, PosTenantConfig } from '@/hooks/use-pos-config';
import { useLocationStore } from '@/stores/location-store';
import { useLocations } from '@/hooks/use-locations';
import { useAuthStore } from '@/stores/auth-store';
import { toast } from '@/stores/toast-store';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useBarcodeSettings, useUpdateBarcodeSettings } from '@/hooks/use-barcode-settings';
import { useBusinessBranding } from '@/hooks/use-business-branding';
import { useDeleteLocationLogo, useLocationLogo, useUploadLocationLogo } from '@/hooks/use-location-logo';
import { isPhotographic, loadImageFromFile, toGrayscalePng } from '@/lib/receipt-logo';
import { ReceiptSamplePreview } from '@/components/receipt-sample-preview';
import { usePersistentTab } from '@/hooks/use-persistent-tab';
import { usePermissions } from '@/hooks/use-permissions';
import {
  CUSTOMER_DISPLAY_GREETING_MAX,
  DEFAULT_CUSTOMER_DISPLAY_GREETING,
  GREETING_PLACEHOLDERS,
  renderCustomerDisplayGreeting,
} from '@/lib/customer-display-greeting';
import { SpendRewards } from './pos-config-spend-rewards';

// The per-location Customer Engagement toggle used to live here as a
// super-admin-only sub-section. It moved to AdminTenantDetailPage because
// super_admin uses the AdminRouter exclusively (see app.tsx) and never lands
// on this page. Non-super-admin users continue to see the rest of POS Config.

type Section = 'registers' | 'config' | 'permissions' | 'cost-visibility';

/** Sub-tab keys, deep-linkable as `?section=` alongside the parent's `?tab=`
 *  — e.g. `#/pos-management?tab=pos-config&section=config` opens straight to
 *  Store Config instead of landing on Registers. */
const VALID_SECTIONS: readonly Section[] = ['registers', 'config', 'permissions', 'cost-visibility'];

export function PosConfigPage() {
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const [section, setSection] = usePersistentTab<Section>(VALID_SECTIONS, 'registers', 'section');
  const devicesQuery = usePosDevices(selectedLocationId || undefined);
  const configQuery = usePosTenantConfig(selectedLocationId || undefined);
  const permsQuery = usePosRolePermissions(selectedLocationId || undefined);
  const costConfigQuery = useProductsPosConfig(selectedLocationId);

  const refresh = () => {
    devicesQuery.refetch();
    configQuery.refetch();
    permsQuery.refetch();
    costConfigQuery.refetch();
  };

  const sections: { key: Section; label: string; icon: React.ElementType }[] = [
    { key: 'registers', label: 'Registers', icon: Monitor },
    { key: 'config', label: 'Store Settings', icon: Store },
    { key: 'permissions', label: 'Role Permissions', icon: Shield },
    { key: 'cost-visibility', label: 'Cost Visibility', icon: Eye },
  ];

  if (!selectedLocationId) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        Select a location from the top to configure POS for that location.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Location context banner — all sections are location-scoped */}
      {selectedLocationName && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Building2 className="h-3.5 w-3.5" />
          <span>Configuring POS for</span>
          <span className="font-medium text-foreground">{selectedLocationName}</span>
        </div>
      )}

      {/* Section sub-tabs + refresh */}
      <div className="flex items-center justify-between border-b border-border">
        <div className="flex">
          {sections.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => setSection(s.key)}
                className={`relative px-4 py-2 text-xs font-medium transition-colors flex items-center gap-2 ${
                  section === s.key ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {s.label}
                {section === s.key && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
              </button>
            );
          })}
        </div>
        <button
          onClick={refresh}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {section === 'registers' && <RegistersSection devicesQuery={devicesQuery} />}
      {section === 'config' && <StoreConfigSection configQuery={configQuery} />}
      {section === 'permissions' && <RolePermissionsSection permsQuery={permsQuery} />}
      {section === 'cost-visibility' && (
        <CostVisibilitySection locationId={selectedLocationId} costConfigQuery={costConfigQuery} />
      )}
    </div>
  );
}

// ───────── Cost Visibility ─────────

const ALL_ROLES = ['super_admin', 'owner', 'admin', 'manager', 'employee'] as const;
const ROLE_LABELS: Record<(typeof ALL_ROLES)[number], string> = {
  super_admin: 'Super Admin',
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  employee: 'Employee',
};

function CostVisibilitySection({
  locationId,
  costConfigQuery,
}: {
  locationId: string | null;
  costConfigQuery: ReturnType<typeof useProductsPosConfig>;
}) {
  const update = useUpdateProductsPosConfig();
  const initialRoles = costConfigQuery.data?.costVisibleRoles?.length
    ? costConfigQuery.data.costVisibleRoles
    : [...DEFAULT_COST_VISIBLE_ROLES];
  const [selected, setSelected] = useState<string[] | null>(null);
  const value = selected ?? initialRoles;

  if (!locationId) return null;
  if (costConfigQuery.isLoading) return <Loading />;

  const toggle = (role: string) => {
    setSelected((cur) => {
      const next = cur ?? initialRoles;
      return next.includes(role) ? next.filter((r) => r !== role) : [...next, role];
    });
  };

  const dirty = selected !== null && JSON.stringify([...selected].sort()) !== JSON.stringify([...initialRoles].sort());

  const save = async () => {
    if (!selected || !selected.length) return;
    await update.mutateAsync({ locationId, costVisibleRoles: selected });
    setSelected(null);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-white p-6">
        <h3 className="mb-1 text-base font-semibold">Who can see cost prices?</h3>
        <p className="mb-4 text-xs text-muted-foreground">
          Pick the roles allowed to view product cost, vendor price, and PO costs at this location. Roles not selected
          will see selling price and other fields, but cost columns are hidden from them.
        </p>
        <div className="flex flex-wrap gap-2">
          {ALL_ROLES.map((role) => {
            const on = value.includes(role);
            return (
              <button
                key={role}
                type="button"
                onClick={() => toggle(role)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  on
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-white text-foreground hover:bg-muted'
                }`}
              >
                {ROLE_LABELS[role]}
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={save}
            disabled={!dirty || !value.length || update.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" />
            Save
          </button>
          {dirty && (
            <button
              onClick={() => setSelected(null)}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              Cancel
            </button>
          )}
          {!value.length && <span className="text-xs text-red-600">Pick at least one role.</span>}
        </div>
      </div>
    </div>
  );
}

// ───────── Registers ─────────

function RegistersSection({ devicesQuery }: { devicesQuery: ReturnType<typeof usePosDevices> }) {
  const devices = devicesQuery.data?.data ?? [];
  const active = devices.filter((d) => d.isActive === 'true' || String(d.isActive) === 'true');
  const inactive = devices.filter((d) => !(d.isActive === 'true' || String(d.isActive) === 'true'));

  // Deregister-and-delete flow. The dashboard role guard (admin+) on the
  // backend is the source of truth — the button is shown for everyone but
  // the click will 403 for non-admins. We surface the row's name in the
  // confirm dialog so the operator can't get the wrong row.
  const deleteRegister = useDeletePosRegister();
  const [confirmTarget, setConfirmTarget] = useState<{
    registerId: string;
    name: string;
  } | null>(null);

  const handleConfirm = () => {
    if (!confirmTarget) return;
    const { registerId, name } = confirmTarget;
    deleteRegister.mutate(
      { registerId },
      {
        onSuccess: (resp) => {
          if (resp.posBackendOk) {
            toast.success(`Deregistered "${name}" — device credentials revoked and row removed`);
          } else {
            toast.warning(
              `Removed "${name}" from dashboard, but pos-backend reported "${resp.posBackendError ?? 'unknown'}". Device may still hold credentials — verify upstream.`,
            );
          }
          setConfirmTarget(null);
        },
        onError: (err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Failed to deregister register';
          toast.error(msg);
        },
      },
    );
  };

  if (devicesQuery.isLoading) return <Loading />;

  return (
    <div className="space-y-4">
      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Total" value={devices.length} tone="neutral" />
        <StatCard label="Active" value={active.length} tone="success" />
        <StatCard label="Inactive" value={inactive.length} tone="warning" />
      </div>

      {/* Registers table */}
      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-xs">Register Name</th>
              <th className="px-3 py-2 text-left font-medium text-xs">Register ID</th>
              <th className="px-3 py-2 text-left font-medium text-xs">Status</th>
              <th className="px-3 py-2 text-left font-medium text-xs">Last Seen</th>
              <th className="px-3 py-2 text-right font-medium text-xs">Actions</th>
            </tr>
          </thead>
          <tbody>
            {devices.length === 0 && (
              <tr>
                <td colSpan={5}>
                  <EmptyState
                    icon={Monitor}
                    title="No registers yet"
                    description="POS devices appear here once they register with this location."
                    className="py-8"
                  />
                </td>
              </tr>
            )}
            {devices.map((d) => {
              const isActive = d.isActive === 'true' || String(d.isActive) === 'true';
              return (
                <tr key={d.id} className="border-t border-border">
                  <td className="px-3 py-2 font-medium">{d.deviceName ?? '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                    {d.registerId ? d.registerId.substring(0, 8) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                        isActive
                          ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                      }`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {d.lastSeenAt ? new Date(d.lastSeenAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() =>
                        setConfirmTarget({
                          registerId: d.posDeviceId ?? d.registerId ?? d.id,
                          name: d.deviceName ?? 'this register',
                        })
                      }
                      title="Deregister and remove this register"
                      className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={deleteRegister.isPending}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Deregister
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!confirmTarget}
        title="Deregister register"
        message={
          confirmTarget
            ? `Are you sure you want to deregister "${confirmTarget.name}"? This revokes the device's credentials and removes the register so it stops appearing in the POS registration picker. The action can't be undone — the device will have to re-register from scratch to be usable again.`
            : ''
        }
        confirmLabel="Deregister"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteRegister.isPending}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}

// ───────── Store Logo ─────────

/** Reject before upload rather than after: receipts need a few hundred dots,
 *  so anything larger is a scan or hi-res export that only bloats the sync. */
const MAX_LOGO_BYTES = 5 * 1024 * 1024;

/**
 * Receipt logo upload with a print-accurate preview.
 *
 * This is the RECEIPT logo, not the tenant brand mark. The brand mark
 * (business_branding.logo_url) drives the dashboard, POS login screen and
 * customer display and stays super-admin owned; this is separate artwork for a
 * thermal head, stored per location on pos_tenant_config.receipt_logo_url and
 * pushed to that location's registers as receipt_logo_path.
 *
 * The chosen file is converted to grayscale in the browser and the preview
 * shows the dithered, paper-width, height-capped result — so the merchant
 * approves the actual printed output, and the bytes stored are the ones they
 * saw. Location-specific: each store uploads, replaces or deletes its own
 * logo, and only that store's receipts change.
 */
export function StoreLogoBlock({
  locationId,
  storeName,
  addressLine,
  cityLine,
}: {
  locationId: string;
  storeName: string;
  addressLine?: string;
  cityLine?: string;
}) {
  const tenant = useAuthStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);
  const role = user?.role;
  // `/tenants/me` populates the store for merchant users but can fail, and is
  // skipped for super admins — fall back to the id on the user itself.
  const tenantId = tenant?.id ?? user?.tenantId ?? '';
  const { data } = useLocationLogo('receipt-logo', tenantId, locationId);
  const uploadReceiptLogo = useUploadLocationLogo();
  const deleteReceiptLogo = useDeleteLocationLogo();

  const [pendingImg, setPendingImg] = useState<HTMLImageElement | null>(null);
  const [pending, setPending] = useState<{ blob: Blob; name: string } | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const busy = uploadReceiptLogo.isPending || deleteReceiptLogo.isPending;

  const canUpload = role === 'super_admin' || role === 'owner' || role === 'admin';
  // Role is a real permission boundary. A missing tenant is not — that is a
  // session problem, so the panel still renders and says so instead of
  // vanishing and leaving an admin hunting for a control that is not there.
  if (!canUpload) return null;

  const savedUrl = data?.data?.url ?? null;

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset now so re-picking the same file still fires onChange.
    e.target.value = '';
    if (!file) return;
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('Please choose a PNG or JPG image.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError('Logo must be under 5 MB.');
      return;
    }
    try {
      const img = await loadImageFromFile(file);
      setPendingImg(img);
      setPending({ blob: await toGrayscalePng(img), name: file.name });
      // Warn rather than block: it is the merchant's logo, and a thermal head
      // is simply a poor medium for a photograph. The preview beside it shows
      // what the warning means.
      setWarning(
        isPhotographic(img)
          ? 'This looks like a photo or gradient. Receipt printers are one-bit black and white, so shaded artwork prints as a dot pattern. A flat, high-contrast logo prints far better.'
          : null,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read that image.');
    }
  };

  const onSave = async () => {
    if (!pending || !tenantId) return;
    try {
      await uploadReceiptLogo.mutateAsync({ kind: 'receipt-logo', tenantId, locationId, file: pending.blob });
      setPending(null);
      setPendingImg(null);
      setWarning(null);
      toast.success('Receipt logo saved. Registers at this store pick it up on their next sync.');
    } catch {
      toast.error('Receipt logo upload failed.');
    }
  };

  const onDelete = async () => {
    if (!tenantId) return;
    try {
      await deleteReceiptLogo.mutateAsync({ kind: 'receipt-logo', tenantId, locationId });
      setConfirmDelete(false);
      toast.success('Receipt logo removed. Registers at this store stop printing it on their next sync.');
    } catch {
      toast.error('Could not remove the receipt logo.');
    }
  };

  const onCancel = () => {
    setPending(null);
    setPendingImg(null);
    setWarning(null);
    setError(null);
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-1 text-sm font-semibold text-foreground">Receipt Logo</div>
      <p className="mb-3 text-xs text-muted-foreground">
        Printed at the top of POS receipts at this store only — each location keeps its own logo, separate from your
        brand logo. Receipt printers are black and white, so the image is converted to grayscale — the preview shows
        exactly how it will print on 80mm paper.
      </p>

      <div className="flex flex-wrap items-start gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Current</span>
          <div className="flex h-20 w-40 items-center justify-center rounded border border-border bg-white p-1">
            {savedUrl ? (
              <img src={savedUrl} alt="Receipt logo" className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-xs text-muted-foreground">None</span>
            )}
          </div>
        </div>

        {pendingImg && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Sample receipt (80mm) — how it will print</span>
            <ReceiptSamplePreview
              logo={pendingImg}
              storeName={storeName || 'Your Store'}
              addressLine={addressLine}
              cityLine={cityLine}
            />
          </div>
        )}

        <div className="flex flex-col gap-2">
          <label
            className={`inline-flex items-center gap-2 self-start rounded-md border border-border px-3 py-1.5 text-sm ${
              tenantId ? 'cursor-pointer hover:bg-muted' : 'cursor-not-allowed opacity-50'
            }`}
          >
            <Upload className="h-4 w-4" />
            {savedUrl || pending ? 'Choose Different Image' : 'Choose Image'}
            <input
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              disabled={busy || !tenantId}
              onChange={onPick}
            />
          </label>

          {pending && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onSave}
                disabled={uploadReceiptLogo.isPending}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
              >
                {uploadReceiptLogo.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {uploadReceiptLogo.isPending ? 'Saving...' : 'Save Logo'}
              </button>
              <button
                type="button"
                onClick={onCancel}
                disabled={uploadReceiptLogo.isPending}
                className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          )}

          {savedUrl && !pending && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              className="inline-flex items-center gap-2 self-start rounded-md border border-border px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              Delete Logo
            </button>
          )}

          {error && <p className="text-xs text-destructive">{error}</p>}
          {warning && <p className="max-w-xs text-xs text-amber-600 dark:text-amber-500">{warning}</p>}
          {!tenantId && (
            <p className="text-xs text-muted-foreground">
              No company is attached to this session, so there is nothing to upload against. Sign out and back in, or
              pick a company first.
            </p>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete receipt logo"
        message="Remove the receipt logo for this store? Its registers stop printing it on their next sync. Other locations are not affected."
        confirmLabel={deleteReceiptLogo.isPending ? 'Deleting…' : 'Delete'}
        variant="danger"
        loading={deleteReceiptLogo.isPending}
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

/**
 * Customer-display (CDS) logo — colour artwork shown on the customer-facing
 * screen at ONE store.
 *
 * Location-specific like the receipt logo above, and kept apart from the tenant
 * brand mark (super-admin owned) for the same reason: a merchant changing what
 * their customers see at one store must not touch the login-screen logo at
 * every store. Stored on pos_tenant_config.cds_logo_url and pushed to that
 * location's registers as cds_logo_path; the POS forwards it to the display
 * and falls back to the brand mark for a store with none.
 */
export function CdsLogoBlock({ locationId }: { locationId: string }) {
  const tenant = useAuthStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);
  const role = user?.role;
  const tenantId = tenant?.id ?? user?.tenantId ?? '';
  const { data } = useLocationLogo('cds-logo', tenantId, locationId);
  const uploadLogo = useUploadLocationLogo();
  const deleteLogo = useDeleteLocationLogo();

  const [pending, setPending] = useState<{ file: File; previewUrl: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const busy = uploadLogo.isPending || deleteLogo.isPending;

  const canUpload = role === 'super_admin' || role === 'owner' || role === 'admin';
  if (!canUpload) return null;

  const savedUrl = data?.data?.url ?? null;

  const discardPending = () => {
    if (pending) URL.revokeObjectURL(pending.previewUrl);
    setPending(null);
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      setError('Please choose a PNG or JPG image.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError('Logo must be under 5 MB.');
      return;
    }
    discardPending();
    setPending({ file, previewUrl: URL.createObjectURL(file) });
  };

  const onSave = async () => {
    if (!pending || !tenantId) return;
    try {
      await uploadLogo.mutateAsync({ kind: 'cds-logo', tenantId, locationId, file: pending.file });
      discardPending();
      toast.success('CDS logo saved. Registers at this store pick it up on their next sync.');
    } catch {
      toast.error('CDS logo upload failed.');
    }
  };

  const onDelete = async () => {
    if (!tenantId) return;
    try {
      await deleteLogo.mutateAsync({ kind: 'cds-logo', tenantId, locationId });
      setConfirmDelete(false);
      toast.success('CDS logo removed. The display at this store shows your brand logo on its next sync.');
    } catch {
      toast.error('Could not remove the CDS logo.');
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-1 text-sm font-semibold text-foreground">CDS Logo</div>
      <p className="mb-3 text-xs text-muted-foreground">
        Shown on the customer display at this store only — each location keeps its own. When a store has no CDS logo,
        its display shows your brand logo instead. PNG or JPG; a square image with a transparent or plain background
        looks best.
      </p>

      <div className="flex flex-wrap items-start gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Current</span>
          <div className="flex h-28 w-28 items-center justify-center rounded border border-border bg-white p-2">
            {savedUrl ? (
              <img src={savedUrl} alt="CDS logo" className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-xs text-muted-foreground">None</span>
            )}
          </div>
        </div>

        {pending && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">New — as the display will show it</span>
            <div className="flex h-28 w-28 items-center justify-center rounded border border-border bg-white p-2">
              <img src={pending.previewUrl} alt="CDS logo preview" className="max-h-full max-w-full object-contain" />
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <label
            className={`inline-flex items-center gap-2 self-start rounded-md border border-border px-3 py-1.5 text-sm ${
              tenantId ? 'cursor-pointer hover:bg-muted' : 'cursor-not-allowed opacity-50'
            }`}
          >
            <Upload className="h-4 w-4" />
            {savedUrl || pending ? 'Choose Different Image' : 'Choose Image'}
            <input
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              disabled={busy || !tenantId}
              onChange={onPick}
            />
          </label>

          {pending && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onSave}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
              >
                {uploadLogo.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {uploadLogo.isPending ? 'Saving...' : 'Save Logo'}
              </button>
              <button
                type="button"
                onClick={discardPending}
                disabled={busy}
                className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          )}

          {savedUrl && !pending && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              className="inline-flex items-center gap-2 self-start rounded-md border border-border px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              Delete Logo
            </button>
          )}

          {error && <p className="text-xs text-destructive">{error}</p>}
          {!tenantId && (
            <p className="text-xs text-muted-foreground">
              No company is attached to this session, so there is nothing to upload against. Sign out and back in, or
              pick a company first.
            </p>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete CDS logo"
        message="Remove the customer display logo for this store? Its display shows your brand logo instead on the next sync. Other locations are not affected."
        confirmLabel={deleteLogo.isPending ? 'Deleting…' : 'Delete'}
        variant="danger"
        loading={deleteLogo.isPending}
        onConfirm={onDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

// ───────── Store Config ─────────

type ConfigFormState = {
  storeName: string;
  storePhone: string;
  storeAddress: string;
  city: string;
  state: string;
  currency: string;
  currencySymbol: string;
  salesTaxRate: string;
  customFee1Label: string;
  customFee1Rate: string;
  customFee2Label: string;
  customFee2Rate: string;
  receiptHeader: string;
  receiptFooter: string;
  primaryColorHex: string;
  accentColorHex: string;
  secondaryColorHex: string;
  customerDisplayRewardsEnabled: boolean;
  /** Member greeting on the customer display; '' = the default. */
  customerDisplayGreeting: string;
  allowPriceOverride: boolean;
  receiptSmsEnabled: boolean;
  receiptEmailEnabled: boolean;
  // Whether receipts print the store logo. Dashboard-owned: the POS has no
  // register-local switch for this, so what is set here is what prints.
  // Meaningful only when the tenant has logo artwork — see hasLogoArtwork.
  showLogoOnReceipt: boolean;
  // Tri-state: null = inherit the POS-local tip toggle, true/false force the
  // customer-display tip prompt on/off for this location from the dashboard.
  tipEnabledDashboardOverride: boolean | null;
  // Comma-separated tip % presets shown on the CDS tip prompt (e.g. '10,20,30').
  tipPresets: string;
  // Optional checkout tenders, persisted into enabled_features. Cheque/Others
  // default ON (POS reads them with isEnabled → true when unset); EBT, Customer
  // Credit and Store Credit default OFF (isExplicitlyEnabled → only on when set
  // true).
  tenderCheque: boolean;
  tenderOther: boolean;
  tenderEbt: boolean;
  tenderCustomerCredit: boolean;
  tenderStoreCredit: boolean;
  // When on, a hardware-scanned code that matches no product prompts the
  // cashier (admin-tier) to add it as a new product. Persisted into
  // enabled_features as add_product_on_scan; default OFF (isExplicitlyEnabled).
  addProductOnScan: boolean;
  // Same, but for a code typed on the POS PLU/Barcode keypad (manual entry).
  // Independent of addProductOnScan so a tenant can enable one without the
  // other. Persisted as add_product_on_plu; default OFF.
  addProductOnPlu: boolean;
  // Lets a cashier take an amount off an order with a reason (damage, return,
  // bottle refund), recorded as its own negative line. Persisted as
  // minus_amount; default OFF (isExplicitlyEnabled).
  minusAmount: boolean;
  // Toast on the register when ringing up an item leaves its stock at or below
  // the product's reorder threshold. Persisted as low_stock_alert; default OFF
  // (isExplicitlyEnabled).
  lowStockAlert: boolean;
  // Variable-price ("embedded price") barcode layout the POS should parse.
  // Persisted as the string variable_barcode_pattern in enabled_features; empty
  // = off. One of the fixed layout ids (e.g. '2XXXXXAPPPPC').
  variableBarcodePattern: string;
  // How many days of past orders each register keeps and downloads: a preset
  // (30/60/90) or a custom 1–365. Persisted as the number txn_history_days in
  // enabled_features; unset = 90.
  orderHistoryDays: string;
  orderHistoryCustom: boolean;
};

// Choices for how many days of past orders each register keeps; any whole
// number of days from 1 to 365 can also be entered as a custom value.
const ORDER_HISTORY_DAYS = ['30', '60', '90'];
const ORDER_HISTORY_MAX_DAYS = 365;

function validHistoryDays(v: unknown): number {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 && n <= ORDER_HISTORY_MAX_DAYS ? n : 90;
}

const EMPTY_CONFIG: ConfigFormState = {
  storeName: '',
  storePhone: '',
  storeAddress: '',
  city: '',
  state: '',
  currency: 'USD',
  currencySymbol: '$',
  // Matches the POS Drift default so first-time saves on a fresh location
  // don't silently push 0% tax to devices.
  salesTaxRate: '8.25',
  customFee1Label: 'Custom Fee 1',
  customFee1Rate: '0',
  customFee2Label: 'Custom Fee 2',
  customFee2Rate: '0',
  receiptHeader: '',
  receiptFooter: '',
  primaryColorHex: '#00838D',
  accentColorHex: '#FFFFFF',
  secondaryColorHex: '#005F66',
  customerDisplayRewardsEnabled: false,
  customerDisplayGreeting: '',
  allowPriceOverride: false,
  receiptSmsEnabled: false,
  receiptEmailEnabled: false,
  showLogoOnReceipt: false,
  tipEnabledDashboardOverride: null,
  tipPresets: '10,20,30',
  tenderCheque: true,
  tenderOther: true,
  tenderEbt: false,
  tenderCustomerCredit: false,
  tenderStoreCredit: false,
  addProductOnScan: false,
  addProductOnPlu: false,
  minusAmount: false,
  lowStockAlert: false,
  variableBarcodePattern: '',
  orderHistoryDays: '90',
  orderHistoryCustom: false,
};

/// Minimal shape of a location row from the products API — locations carry a
/// single free-text address (may include city/state/zip), unlike the tenant's
/// split street/city/state fields.
type LocationRecord = {
  name?: string | null;
  address?: string | null;
} | null;

type TenantRecord = {
  name?: string | null;
  phone?: string | null;
  street?: string | null;
  city?: string | null;
  state?: string | null;
} | null;

/// Build the initial form state for a fresh (no saved config) location.
/// Pre-populates store identity from the selected location first (each
/// location has its own name/address), falling back to the tenant record so
/// receipts carry real business info out of the box — user can override any
/// field. City/state only fall back to tenant when the location has no
/// address of its own: the location address is free-text and already carries
/// city/state, so mixing in the primary account's city/state would show the
/// wrong address on a location that has one.
function configFromLocation(location: LocationRecord, tenant: TenantRecord): ConfigFormState {
  if (!location && !tenant) return EMPTY_CONFIG;
  return {
    ...EMPTY_CONFIG,
    storeName: location?.name || tenant?.name || '',
    storePhone: tenant?.phone ?? '',
    storeAddress: location?.address || tenant?.street || '',
    city: location?.address ? '' : (tenant?.city ?? ''),
    state: location?.address ? '' : (tenant?.state ?? ''),
  };
}

function StoreConfigSection({ configQuery }: { configQuery: ReturnType<typeof usePosTenantConfig> }) {
  const { selectedLocationId } = useLocationStore();
  const locationsQuery = useLocations();
  // Wait for locations too — the form's initial state is computed once on
  // mount (useState initializer), so the selected location's address must be
  // available before the form renders or the prefill would miss it.
  if (configQuery.isLoading || locationsQuery.isLoading) return <Loading />;
  if (!selectedLocationId) return null;
  const location = (locationsQuery.data?.data ?? []).find((l: { id: string }) => l.id === selectedLocationId) ?? null;
  return (
    <StoreConfigForm
      c={configQuery.data?.data ?? null}
      location={location}
      locationId={selectedLocationId}
      key={`${selectedLocationId}-${configQuery.data?.data?.id ?? 'empty'}`}
    />
  );
}

function StoreConfigForm({
  c,
  location,
  locationId,
}: {
  c: PosTenantConfig | null;
  location: LocationRecord;
  locationId: string;
}) {
  const update = useUpdatePosTenantConfig();
  const tenant = useAuthStore((s) => s.tenant);
  const brandingUser = useAuthStore((s) => s.user);
  // Per-location super-admin gate. When false, the Brand Colors block is
  // hidden here and pos-backend nulls the color hexes downstream so the POS
  // app falls back to its default theme. React Query dedupes this with the
  // sibling useProductsPosConfig() call in PosConfigPage — no extra fetch.
  // Default to true while loading so we don't briefly hide the section before
  // the column default arrives.
  const { data: brandingGate } = useProductsPosConfig(locationId);
  const brandColorsEnabled = brandingGate ? brandingGate.brandColorsEnabled === true : true;
  // Per-location super-admin Store Credit gate. Off (the default) hides the
  // Store Credit tender toggle and saves it as off; the server enforces it too.
  // It gates spend rewards as well, which are promotions (promotions.view).
  const storeCreditAllowed = brandingGate?.storeCreditEnabled === true;
  const canViewSpendRewards = usePermissions().has('promotions.view');
  // Whether there is any logo for a receipt to print. Mirrors what the POS
  // resolves — this location's receipt logo first, the colour brand mark as
  // the fallback — so the switch below cannot promise a logo the registers
  // would not receive. React Query dedupes both with StoreLogoBlock's own
  // calls. The tenant-id fallback is that block's too: `/tenants/me` populates
  // the store for merchant users but can fail, and is skipped for super
  // admins, and without it the switch would sit permanently disabled for them.
  const brandingTenantId = tenant?.id ?? brandingUser?.tenantId ?? '';
  const { data: branding } = useBusinessBranding(brandingTenantId);
  const { data: locationLogo } = useLocationLogo('receipt-logo', brandingTenantId, locationId);
  const hasLogoArtwork = Boolean(locationLogo?.data?.url || branding?.data?.logoUrl);
  const [form, setForm] = useState<ConfigFormState>(() => {
    if (!c) return configFromLocation(location, tenant);
    // Fall back to the location's own values (then tenant) when a saved row
    // exists but individual fields are blank — the old form defaulted
    // everything to empty strings.
    const fallback = configFromLocation(location, tenant);
    return {
      storeName: c.storeName || fallback.storeName,
      storePhone: c.storePhone || fallback.storePhone,
      storeAddress: c.storeAddress || fallback.storeAddress,
      city: c.city || fallback.city,
      state: c.state || fallback.state,
      currency: c.currency ?? 'USD',
      currencySymbol: c.currencySymbol ?? '$',
      salesTaxRate: c.salesTaxRate ?? '8.25',
      customFee1Label: c.customFee1Label ?? 'Custom Fee 1',
      customFee1Rate: c.customFee1Rate ?? '0',
      customFee2Label: c.customFee2Label ?? 'Custom Fee 2',
      customFee2Rate: c.customFee2Rate ?? '0',
      receiptHeader: c.receiptHeader ?? '',
      receiptFooter: c.receiptFooter ?? '',
      primaryColorHex: c.primaryColorHex ?? '#00838D',
      accentColorHex: c.accentColorHex ?? '#FFFFFF',
      secondaryColorHex: c.secondaryColorHex ?? '#005F66',
      customerDisplayRewardsEnabled: c.customerDisplayRewardsEnabled ?? false,
      customerDisplayGreeting: c.customerDisplayGreeting ?? '',
      allowPriceOverride: c.allowPriceOverride ?? false,
      receiptSmsEnabled: c.receiptSmsEnabled ?? false,
      receiptEmailEnabled: c.receiptEmailEnabled ?? false,
      // null means the switch has never been set, which prints no logo.
      showLogoOnReceipt: c.showLogoOnReceiptDashboardOverride ?? false,
      // Preserve null (= inherit) distinctly from false (= force off).
      tipEnabledDashboardOverride: c.tipEnabledDashboardOverride ?? null,
      tipPresets: c.tipPresets ?? '10,20,30',
      // Tenders live in enabled_features. Mirror the POS default semantics:
      // cheque/other are on unless explicitly false; ebt/credit are off unless
      // explicitly true.
      tenderCheque: (c.enabledFeatures as Record<string, unknown> | null)?.['tender_cheque'] !== false,
      tenderOther: (c.enabledFeatures as Record<string, unknown> | null)?.['tender_other'] !== false,
      tenderEbt: (c.enabledFeatures as Record<string, unknown> | null)?.['tender_ebt'] === true,
      tenderCustomerCredit: (c.enabledFeatures as Record<string, unknown> | null)?.['tender_customer_credit'] === true,
      tenderStoreCredit: (c.enabledFeatures as Record<string, unknown> | null)?.['tender_store_credit'] === true,
      // Opt-in: only on when explicitly true.
      addProductOnScan: (c.enabledFeatures as Record<string, unknown> | null)?.['add_product_on_scan'] === true,
      addProductOnPlu: (c.enabledFeatures as Record<string, unknown> | null)?.['add_product_on_plu'] === true,
      minusAmount: (c.enabledFeatures as Record<string, unknown> | null)?.['minus_amount'] === true,
      lowStockAlert: (c.enabledFeatures as Record<string, unknown> | null)?.['low_stock_alert'] === true,
      variableBarcodePattern: String(
        (c.enabledFeatures as Record<string, unknown> | null)?.['variable_barcode_pattern'] ?? '',
      ),
      ...(() => {
        const days = validHistoryDays((c.enabledFeatures as Record<string, unknown> | null)?.['txn_history_days']);
        return {
          orderHistoryDays: String(days),
          orderHistoryCustom: !ORDER_HISTORY_DAYS.includes(String(days)),
        };
      })(),
    };
  });
  const [saved, setSaved] = useState(false);

  // Auto-barcode numbering lives in the products service and is TENANT-WIDE —
  // a barcode identifies one product across the whole business — unlike every
  // other setting on this page, which is per-location. So it loads and saves on
  // its own path rather than riding this location's enabled_features.
  //
  // Kept out of `form` and derived instead: the saved values are whatever the
  // query returns until the operator types, at which point the draft takes
  // over. Syncing the query into form state via an effect would mean a
  // setState-in-effect cascade (and lint rightly rejects it).
  const barcodeGateOn = tenant?.barcodeAutogenEnabled === true;
  const barcodeQuery = useBarcodeSettings(barcodeGateOn);
  const updateBarcode = useUpdateBarcodeSettings();
  const barcode = barcodeQuery.data?.data;
  const [barcodeError, setBarcodeError] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [barcodeDraft, setBarcodeDraft] = useState<{ start: string; step: string } | null>(null);
  const barcodeStart = barcodeDraft?.start ?? barcode?.barcodeStart ?? '';
  const barcodeStep = barcodeDraft?.step ?? String(barcode?.barcodeStep ?? 5);
  const setBarcodeField = (key: 'start' | 'step', value: string) => {
    setBarcodeDraft({ start: barcodeStart, step: barcodeStep, [key]: value });
    setBarcodeError(null);
    setSaved(false);
  };

  const setField = <K extends keyof ConfigFormState>(key: K, value: ConfigFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setHistoryError(null);
    const historyDays = Number(form.orderHistoryDays);
    if (!Number.isInteger(historyDays) || historyDays < 1 || historyDays > ORDER_HISTORY_MAX_DAYS) {
      setHistoryError(`Enter a whole number of days from 1 to ${ORDER_HISTORY_MAX_DAYS}.`);
      return;
    }
    // Auto-barcode numbering is tenant-wide and owned by the products service,
    // so it saves first and independently. A validation failure there (bad
    // check digit, reserved prefix, rewinding past issued codes) must stop the
    // whole save — otherwise the operator sees "Saved" and the barcode fields
    // silently didn't take.
    setBarcodeError(null);
    let barcodeConfigured = false;
    if (barcodeGateOn) {
      const start = barcodeStart.trim();
      const step = Number(barcodeStep);
      try {
        await updateBarcode.mutateAsync({
          barcodeStart: start || null,
          barcodeStep: Number.isFinite(step) && step >= 1 ? Math.round(step) : 5,
        });
        barcodeConfigured = !!start;
      } catch (err: unknown) {
        const e = err as { error?: { message?: string; details?: { message?: string }[] } };
        setBarcodeError(e?.error?.details?.[0]?.message || e?.error?.message || 'Could not save the barcode settings');
        return;
      }
    }

    // Merge the tender toggles into the existing enabled_features so other POS
    // feature flags (rewards, kds, tips, …) on the saved config are preserved.
    const enabledFeatures = {
      ...((c?.enabledFeatures as Record<string, unknown> | null) ?? {}),
      tender_cheque: form.tenderCheque,
      tender_other: form.tenderOther,
      tender_ebt: form.tenderEbt,
      tender_customer_credit: form.tenderCustomerCredit,
      tender_store_credit: storeCreditAllowed && form.tenderStoreCredit,
      add_product_on_scan: form.addProductOnScan,
      add_product_on_plu: form.addProductOnPlu,
      minus_amount: form.minusAmount,
      low_stock_alert: form.lowStockAlert,
      variable_barcode_pattern: form.variableBarcodePattern,
      txn_history_days: historyDays,
      // Single flag the POS gates its Generate button on: true only when our
      // team has enabled the tenant AND the tenant has saved a starting
      // barcode, so the register never offers a button that can only fail.
      // The super-admin toggle forces this false when it's switched off.
      barcode_autogen: barcodeGateOn && barcodeConfigured,
    };
    const result = (await update.mutateAsync({
      locationId,
      enabled_features: enabledFeatures,
      store_name: form.storeName,
      store_phone: form.storePhone,
      store_address: form.storeAddress,
      city: form.city,
      state: form.state,
      currency: form.currency,
      currency_symbol: form.currencySymbol,
      sales_tax_rate: form.salesTaxRate,
      custom_fee_1_label: form.customFee1Label,
      custom_fee_1_rate: form.customFee1Rate,
      custom_fee_2_label: form.customFee2Label,
      custom_fee_2_rate: form.customFee2Rate,
      receipt_header: form.receiptHeader,
      receipt_footer: form.receiptFooter,
      primary_color_hex: form.primaryColorHex,
      accent_color_hex: form.accentColorHex,
      secondary_color_hex: form.secondaryColorHex,
      customer_display_rewards_enabled: form.customerDisplayRewardsEnabled,
      // Blank is saved as null — the display's default greeting.
      customer_display_greeting: form.customerDisplayGreeting,
      allow_price_override: form.allowPriceOverride,
      receipt_sms_enabled: form.receiptSmsEnabled,
      receipt_email_enabled: form.receiptEmailEnabled,
      // Never push a logo the registers have no artwork for: a tenant who
      // deletes their logo while the switch is on would otherwise leave every
      // receipt asking for a picture that is not there.
      show_logo_on_receipt_dashboard_override: hasLogoArtwork && form.showLogoOnReceipt,
      // null is meaningful (inherit) — sent explicitly so the route can clear
      // a prior override; the API uses a presence check, not `??`.
      tip_enabled_dashboard_override: form.tipEnabledDashboardOverride,
      tip_presets: form.tipPresets,
    })) as { success: boolean; posPushOk?: boolean };
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    if (result?.posPushOk === false) {
      toast.error(
        'Config saved to dashboard but failed to push to POS devices. POS devices may not reflect this change.',
      );
    }
  };

  const fieldClass =
    'w-full rounded border border-border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary';

  // Keep hex inputs valid: drop any non-hex chars (spaces, punctuation, G-Z),
  // cap at 6 digits, and always re-prefix `#` so the user can't delete it.
  const sanitizeHex = (raw: string) => {
    const cleaned = raw
      .replace(/[^0-9a-fA-F]/g, '')
      .slice(0, 6)
      .toUpperCase();
    return '#' + cleaned;
  };

  return (
    <div className="space-y-7">
      {!c && (
        <div className="rounded border border-dashed border-border p-3 text-xs text-muted-foreground">
          No config yet. Fill in defaults and save — this will become the config for POS devices.
        </div>
      )}

      <Band
        title="Store record"
        description="The business identity printed on receipts and mirrored to every device at this store."
        icon={Store}
        rule="border-slate-300"
        tint="text-slate-500"
      >
        <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
          <SettingsCard title="Business details">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Store Name">
                <input
                  className={fieldClass}
                  value={form.storeName}
                  onChange={(e) => setField('storeName', e.target.value)}
                />
              </Field>
              <Field label="Phone">
                <input
                  className={fieldClass}
                  value={form.storePhone}
                  onChange={(e) => setField('storePhone', e.target.value)}
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Address">
                  <input
                    className={fieldClass}
                    value={form.storeAddress}
                    onChange={(e) => setField('storeAddress', e.target.value)}
                  />
                </Field>
              </div>
              <Field label="City">
                <input className={fieldClass} value={form.city} onChange={(e) => setField('city', e.target.value)} />
              </Field>
              <Field label="State">
                <input className={fieldClass} value={form.state} onChange={(e) => setField('state', e.target.value)} />
              </Field>
            </div>
          </SettingsCard>

          <SettingsCard title="Currency, tax & fees">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field label="Currency">
                <input
                  className={fieldClass}
                  value={form.currency}
                  onChange={(e) => setField('currency', e.target.value.toUpperCase())}
                  maxLength={3}
                />
              </Field>
              <Field label="Currency Symbol">
                <input
                  className={fieldClass}
                  value={form.currencySymbol}
                  onChange={(e) => setField('currencySymbol', e.target.value)}
                  maxLength={3}
                />
              </Field>
              <Field label="Sales Tax Rate (%)">
                <input
                  className={fieldClass}
                  type="number"
                  step="0.001"
                  value={form.salesTaxRate}
                  onChange={(e) => setField('salesTaxRate', e.target.value)}
                />
              </Field>
            </div>

            <div className="mt-4 border-t border-border pt-4">
              <div className="mb-1 text-xs font-semibold text-foreground">Additional Service Fees</div>
              <div className="mb-2 text-[11px] text-muted-foreground">
                Renameable percentage fees applied to the whole order at checkout (alongside Sales Tax, regardless of
                per-product taxable flags). Set rate to 0 to disable.
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div className="grid grid-cols-[1fr_110px] gap-2">
                  <Field label="Custom Fee 1 — Label">
                    <input
                      className={fieldClass}
                      value={form.customFee1Label}
                      onChange={(e) => setField('customFee1Label', e.target.value.slice(0, 50))}
                      maxLength={50}
                    />
                  </Field>
                  <Field label="Rate (%)">
                    <input
                      className={fieldClass}
                      type="number"
                      step="0.001"
                      min="0"
                      value={form.customFee1Rate}
                      onChange={(e) => setField('customFee1Rate', e.target.value)}
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-[1fr_110px] gap-2">
                  <Field label="Custom Fee 2 — Label">
                    <input
                      className={fieldClass}
                      value={form.customFee2Label}
                      onChange={(e) => setField('customFee2Label', e.target.value.slice(0, 50))}
                      maxLength={50}
                    />
                  </Field>
                  <Field label="Rate (%)">
                    <input
                      className={fieldClass}
                      type="number"
                      step="0.001"
                      min="0"
                      value={form.customFee2Rate}
                      onChange={(e) => setField('customFee2Rate', e.target.value)}
                    />
                  </Field>
                </div>
              </div>
            </div>
          </SettingsCard>
        </div>
      </Band>

      <Band
        title="POS terminal"
        description="What the cashier sees and can do at the register."
        icon={Monitor}
        rule="border-slate-700"
        tint="text-slate-700"
      >
        <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
          {brandColorsEnabled && (
            <SettingsCard
              title="Register appearance"
              description="Themes the POS app itself — not the customer display."
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Primary Color">
                  <div className="flex gap-2">
                    <input
                      type="color"
                      aria-label="Primary color picker"
                      className="h-8 w-12 rounded border border-border cursor-pointer"
                      value={form.primaryColorHex}
                      onChange={(e) => setField('primaryColorHex', e.target.value)}
                    />
                    <input
                      className={fieldClass}
                      value={form.primaryColorHex}
                      onChange={(e) => setField('primaryColorHex', sanitizeHex(e.target.value))}
                      maxLength={7}
                      spellCheck={false}
                    />
                  </div>
                </Field>
                <Field label="Secondary Color">
                  <div className="flex gap-2">
                    <input
                      type="color"
                      aria-label="Secondary color picker"
                      className="h-8 w-12 rounded border border-border cursor-pointer"
                      value={form.secondaryColorHex}
                      onChange={(e) => setField('secondaryColorHex', e.target.value)}
                    />
                    <input
                      className={fieldClass}
                      value={form.secondaryColorHex}
                      onChange={(e) => setField('secondaryColorHex', sanitizeHex(e.target.value))}
                      maxLength={7}
                      spellCheck={false}
                    />
                  </div>
                </Field>
                <Field label="Accent Color">
                  <div className="flex gap-2">
                    <input
                      type="color"
                      aria-label="Accent color picker"
                      className="h-8 w-12 rounded border border-border cursor-pointer"
                      value={form.accentColorHex}
                      onChange={(e) => setField('accentColorHex', e.target.value)}
                    />
                    <input
                      className={fieldClass}
                      value={form.accentColorHex}
                      onChange={(e) => setField('accentColorHex', sanitizeHex(e.target.value))}
                      maxLength={7}
                      spellCheck={false}
                    />
                  </div>
                </Field>
              </div>
            </SettingsCard>
          )}

          <SettingsCard title="Cart">
            <ToggleRow
              label="Allow price override from POS"
              description="Show a price-edit action on cart line items. Cashiers can apply the new price to the current order only, or persist it to the product at this location. Still gated per-role by canOverridePrice."
              checked={form.allowPriceOverride}
              onChange={(v) => setField('allowPriceOverride', v)}
            />
          </SettingsCard>

          <StoreLogoBlock
            locationId={locationId}
            storeName={form.storeName}
            addressLine={form.storeAddress}
            cityLine={[form.city, form.state].filter(Boolean).join(', ')}
          />

          <SettingsCard title="Receipt content">
            <div className="space-y-3">
              <Field label="Receipt Header">
                <textarea
                  className={fieldClass}
                  rows={2}
                  value={form.receiptHeader}
                  onChange={(e) => setField('receiptHeader', e.target.value)}
                />
              </Field>
              <Field label="Receipt Footer">
                <textarea
                  className={fieldClass}
                  rows={2}
                  value={form.receiptFooter}
                  onChange={(e) => setField('receiptFooter', e.target.value)}
                />
              </Field>
              <div className="border-t border-border pt-3">
                <ToggleRow
                  label="Show store logo on receipt"
                  description={
                    hasLogoArtwork
                      ? 'Print the store logo above the store name on POS receipts. Off by default.'
                      : 'Upload a receipt logo first — there is no artwork to print yet.'
                  }
                  checked={hasLogoArtwork && form.showLogoOnReceipt}
                  disabled={!hasLogoArtwork}
                  onChange={(v) => setField('showLogoOnReceipt', v)}
                />
              </div>
            </div>
          </SettingsCard>

          <SettingsCard title="Products">
            <div className="space-y-3.5">
              <ToggleRow
                label="Add product from barcode scan when not found"
                description="When a code scanned with the hardware scanner matches no product, prompt the cashier to add it as a new product, then open the Add Product dialog pre-filled with that code. Restricted to admin-tier roles; other cashiers just see the 'no product found' message."
                checked={form.addProductOnScan}
                onChange={(v) => setField('addProductOnScan', v)}
              />
              <ToggleRow
                label="Add product from PLU keypad when not found"
                description="When a code typed on the POS PLU/Barcode keypad matches no product, prompt the cashier to add it as a new product, then open the Add Product dialog pre-filled with that code. Independent of the scan toggle above. Restricted to admin-tier roles."
                checked={form.addProductOnPlu}
                onChange={(v) => setField('addProductOnPlu', v)}
              />
              <ToggleRow
                label="Low stock alert"
                description="Show a toast on the register when ringing up an item leaves its stock at or below the product's reorder threshold, so the cashier knows it is time to reorder. Products without a reorder threshold never alert."
                checked={form.lowStockAlert}
                onChange={(v) => setField('lowStockAlert', v)}
              />
            </div>
          </SettingsCard>

          <SettingsCard title="Checkout">
            <ToggleRow
              label="Minus amount on an order"
              description="Lets the cashier take an amount off an order with a reason (Damage, Return, Bottle Refund, Other). It records as its own negative line, so it shows on the receipt and in order detail rather than disappearing into the discount total. The amount can never exceed the order total, and the line is non-taxable."
              checked={form.minusAmount}
              onChange={(v) => setField('minusAmount', v)}
            />
          </SettingsCard>

          <SettingsCard title="Checkout tenders">
            <div className="space-y-3.5">
              <ToggleRow
                label="Cheque"
                description="Show the Cheque tender at checkout."
                checked={form.tenderCheque}
                onChange={(v) => setField('tenderCheque', v)}
              />
              <ToggleRow
                label="Others"
                description="Show the Others tender at checkout for miscellaneous payment types."
                checked={form.tenderOther}
                onChange={(v) => setField('tenderOther', v)}
              />
              <ToggleRow
                label="EBT (SNAP)"
                description="Show the EBT tender. Charges only the EBT-eligible amount and prompts for the remaining balance on another tender."
                checked={form.tenderEbt}
                onChange={(v) => setField('tenderEbt', v)}
              />
              <ToggleRow
                label="Customer Credit Account"
                description="Show the 'Pay by Credit' tender. The cashier looks the customer up by phone; only customers enabled as Credit/Wholesale (Customer Management) can charge up to their available credit, with any remainder collected on another tender."
                checked={form.tenderCustomerCredit}
                onChange={(v) => setField('tenderCustomerCredit', v)}
              />
              {storeCreditAllowed ? (
                <ToggleRow
                  label="Store Credit"
                  description="Let cashiers refund to store credit, take store credit as a tender and sell gift cards. The register prints a code the customer brings back to spend at this location. Needs a connection to the server."
                  checked={form.tenderStoreCredit}
                  onChange={(v) => setField('tenderStoreCredit', v)}
                />
              ) : (
                <p className="text-xs text-muted-foreground">
                  Store credit and gift cards aren&apos;t available at this location. Contact support to turn them on.
                </p>
              )}
            </div>
          </SettingsCard>

          {storeCreditAllowed && canViewSpendRewards && (
            <SettingsCard title="Spend rewards">
              <SpendRewards locationId={locationId} />
            </SettingsCard>
          )}

          <SettingsCard title="Receipt delivery">
            <div className="space-y-3.5">
              <ToggleRow
                label="Text receipt (SMS)"
                description="Offer 'Text' in the POS post-payment receipt prompt so a customer can receive their receipt by SMS. Requires the receipt-delivery backend to be configured for this tenant."
                checked={form.receiptSmsEnabled}
                onChange={(v) => setField('receiptSmsEnabled', v)}
              />
              <ToggleRow
                label="Email receipt"
                description="Offer 'Email' in the POS post-payment receipt prompt so a customer can receive their receipt by email. Requires the receipt-delivery backend to be configured for this tenant."
                checked={form.receiptEmailEnabled}
                onChange={(v) => setField('receiptEmailEnabled', v)}
              />
            </div>
          </SettingsCard>

          {/* Gated by the super-admin per-tenant flag. Unlike everything else on
              this page these two settings are TENANT-WIDE — a barcode identifies
              one product across the whole business, so a per-store series would
              collide. Saved through the products service, not enabled_features. */}
          {barcodeGateOn && (
            <SettingsCard
              title="Barcodes"
              description={
                <>
                  Applies to <span className="font-medium">every store</span> in this business, not just{' '}
                  {form.storeName || 'this one'}.
                </>
              }
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Starting barcode">
                  <input
                    className={fieldClass}
                    value={barcodeStart}
                    inputMode="numeric"
                    placeholder="e.g. 500000000005"
                    maxLength={13}
                    onChange={(e) => setBarcodeField('start', e.target.value.replace(/\D/g, ''))}
                  />
                </Field>
                <Field label="Increment by">
                  <input
                    className={fieldClass}
                    value={barcodeStep}
                    inputMode="numeric"
                    placeholder="5"
                    onChange={(e) => setBarcodeField('step', e.target.value.replace(/\D/g, ''))}
                  />
                </Field>
              </div>

              {barcodeError && <p className="mt-2 text-xs text-red-500">{barcodeError}</p>}

              {/* Show what the series actually looks like. The trailing digit is a
                  check digit and is recomputed per code, so the printed numbers
                  don't step by the increment even though the barcode does. */}
              {barcode?.preview?.length ? (
                <p className="mt-3 text-xs text-muted-foreground">
                  Next codes: <span className="font-mono text-foreground">{barcode.preview.join('  ')}</span>
                </p>
              ) : null}

              <p className="mt-2 text-xs text-muted-foreground">
                Leave the starting barcode blank to switch generation off. It must be a full, valid 12-digit (UPC-A) or
                13-digit (EAN-13) barcode so generated codes scan like any other product — we&rsquo;ll tell you if the
                check digit is wrong. Every code in this series is marked{' '}
                <span className="font-medium text-foreground">Auto</span> on the product list.
                {barcode?.catalog?.commonLength ? (
                  <>
                    {' '}
                    Most of your products ({barcode.catalog.commonCount} of {barcode.catalog.totalWithBarcode}) use{' '}
                    {barcode.catalog.commonLength}-digit barcodes — match that length here.
                  </>
                ) : null}
              </p>
            </SettingsCard>
          )}

          {/* Gated by the super-admin per-tenant flag: the tenant only sees the
              barcode-format picker once our team enables the feature for them. */}
          {tenant?.variableBarcodeEnabled === true && (
            <SettingsCard title="Variable-price barcodes">
              <Field label="Embedded-price barcode format">
                <select
                  className={fieldClass}
                  value={form.variableBarcodePattern}
                  onChange={(e) => setField('variableBarcodePattern', e.target.value)}
                >
                  <option value="">Off</option>
                  <option value="2XXXXXAPPPPC">2XXXXXAPPPPC — prefix 2, item 5, price 4 (12-digit)</option>
                  <option value="20XXXXXAPPPPC">20XXXXXAPPPPC — prefix 20, item 5, price 4 (13-digit)</option>
                  <option value="20XXXXXXPPPPC">20XXXXXXPPPPC — prefix 20, item 6, price 4 (13-digit)</option>
                  <option value="20XXXXXPPPPPC">20XXXXXPPPPPC — prefix 20, item 5, price 5 (13-digit)</option>
                  <option value="20XXXXAPPPPPC">20XXXXAPPPPPC — prefix 20, item 4, price 5 (13-digit)</option>
                  <option value="20XXXXPPPPPC">20XXXXPPPPPC — prefix 20, item 4, price 5 (12-digit)</option>
                </select>
              </Field>
              <p className="mt-2 text-xs text-muted-foreground">
                When set, the POS parses in-store barcodes of this layout and rings the item at the price embedded in
                the barcode. Legend: X = item / PLU barcode digits, A = ignored flag / decimal indicator, P = price
                (last 2 digits are cents), C = check digit. Leave “Off” to disable.
              </p>
            </SettingsCard>
          )}

          <SettingsCard title="Order history on registers">
            <Field label="Keep past orders on each register for">
              <select
                className={fieldClass}
                value={form.orderHistoryCustom ? 'custom' : form.orderHistoryDays}
                onChange={(e) => {
                  const v = e.target.value;
                  setHistoryError(null);
                  if (v === 'custom') {
                    setField('orderHistoryCustom', true);
                  } else {
                    setField('orderHistoryCustom', false);
                    setField('orderHistoryDays', v);
                  }
                }}
              >
                {ORDER_HISTORY_DAYS.map((d) => (
                  <option key={d} value={d}>
                    Last {d} days
                  </option>
                ))}
                <option value="custom">Custom…</option>
              </select>
            </Field>
            {form.orderHistoryCustom && (
              <Field label="Number of days">
                <input
                  className={fieldClass}
                  type="number"
                  min={1}
                  max={ORDER_HISTORY_MAX_DAYS}
                  step={1}
                  value={form.orderHistoryDays}
                  onChange={(e) => {
                    setHistoryError(null);
                    setField('orderHistoryDays', e.target.value);
                  }}
                />
              </Field>
            )}
            {historyError && <p className="mt-2 text-xs text-red-500">{historyError}</p>}
            <p className="mt-2 text-xs text-muted-foreground">
              Orders from this period appear in the register&rsquo;s Orders screen for every lane at this location and
              can be reprinted or refunded there. Custom can be any number of days from 1 to 365. A shorter period makes
              new registers set up faster. All orders stay in your dashboard reports either way.
            </p>
          </SettingsCard>
        </div>
      </Band>

      <Band
        title="Customer display"
        description="The second screen the shopper reads, driven by the register."
        icon={Tv}
        rule="border-blue-600"
        tint="text-blue-600"
      >
        <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
          <CdsLogoBlock locationId={locationId} />

          <SettingsCard title="Display">
            <ToggleRow
              label="Customer rewards"
              description="Enable customer rewards for this location. Shows a phone-number numpad on the customer-facing display so shoppers can enter their number, and lets cashiers look up or add customers at the register. The POS handles the lookup."
              checked={form.customerDisplayRewardsEnabled}
              onChange={(v) => setField('customerDisplayRewardsEnabled', v)}
            />
          </SettingsCard>

          <GreetingCard
            value={form.customerDisplayGreeting}
            onChange={(v) => setField('customerDisplayGreeting', v)}
            storeName={form.storeName}
            fieldClass={fieldClass}
          />

          <SettingsCard title="Tips">
            <TriStateRow
              label="Tips on customer display"
              description="Prompt the customer to add a tip (5% / 10% / 15% of subtotal, or a custom amount) on the customer-facing display after the cashier picks Cash or Card. 'Inherit' leaves it to the POS device's own tip toggle — use Enabled/Disabled to force it for this location regardless of the device setting."
              value={form.tipEnabledDashboardOverride}
              onChange={(v) => setField('tipEnabledDashboardOverride', v)}
            />
            <div className="mt-4">
              <Field label="Tip presets (%)">
                <input
                  className={fieldClass}
                  value={form.tipPresets}
                  onChange={(e) => setField('tipPresets', e.target.value)}
                  placeholder="10,20,30"
                />
              </Field>
              <p className="mt-1 text-xs text-muted-foreground">
                Comma-separated percentages shown on the customer-display tip prompt (e.g. 10,20,30).
              </p>
            </div>
          </SettingsCard>
        </div>
      </Band>

      <div className="flex items-center justify-between border-t border-border pt-3">
        <div className="text-xs text-muted-foreground">
          {c?.syncedAt ? `Last synced: ${new Date(c.syncedAt).toLocaleString()}` : 'Not yet synced'}
        </div>
        <div className="flex items-center gap-3">
          {saved && (
            <span className="text-xs text-green-600 flex items-center gap-1">
              <Check className="h-3.5 w-3.5" /> Saved — POS will update within 10s
            </span>
          )}
          {update.isError && <span className="text-xs text-red-600">Save failed</span>}
          <button
            onClick={handleSave}
            disabled={update.isPending}
            className="flex items-center gap-2 rounded bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" />
            {update.isPending ? 'Saving…' : 'Save & Push to POS'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** One surface band — the territory header over a group of settings cards.
 *  Store Config drives three different things (the business record, the POS
 *  terminal, the customer display) and used to interleave them: the CDS logo,
 *  the rewards toggle and the tip prompt sat at positions 6, 9 and 18 of a flat
 *  scroll. The band states which device a group reaches before you read it.
 *
 *  The rule weight and icon tint separate the bands; the title and the sentence
 *  say the same thing in words, so nothing here rests on colour alone. */
function Band({
  title,
  description,
  icon: Icon,
  rule,
  tint,
  children,
}: {
  title: string;
  description: string;
  icon: React.ElementType;
  rule: string;
  tint: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-b-2 pb-2 ${rule}`}>
        <Icon className={`h-5 w-5 shrink-0 ${tint}`} />
        <h3 className="text-base font-bold tracking-tight text-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

/** The single card surface used by every settings group in Store Config. Was
 *  an inline div repeated nine times, in two different radius/background
 *  languages depending on where it sat. */
function SettingsCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3">
        <div className="text-sm font-semibold text-foreground">{title}</div>
        {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
      </div>
      {children}
    </div>
  );
}

/** The member greeting a rewards customer sees (and may hear) when they enter
 *  their phone number on the customer display, with a live preview of how the
 *  display will lay it out. */
export function GreetingCard({
  value,
  onChange,
  storeName,
  fieldClass,
}: {
  value: string;
  onChange: (v: string) => void;
  storeName: string;
  /** The form's input styling, so the field matches its neighbours. */
  fieldClass: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const preview = renderCustomerDisplayGreeting(value, { firstName: 'Jana', storeName });

  // Drop a placeholder in at the cursor (or the end), capped like typing is.
  const insert = (token: string) => {
    const el = ref.current;
    const at = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? at;
    const next = (value.slice(0, at) + token + value.slice(end)).slice(0, CUSTOMER_DISPLAY_GREETING_MAX);
    onChange(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + token.length, at + token.length);
    });
  };

  return (
    <SettingsCard
      title="Member greeting"
      description="Shown when a rewards member enters their phone number on the customer display — and read aloud on registers with Voice Greeting switched on (POS → Menu → Devices → Display). Leave blank for the default."
    >
      <Field label="Greeting message">
        <textarea
          ref={ref}
          className={`${fieldClass} resize-none`}
          rows={4}
          maxLength={CUSTOMER_DISPLAY_GREETING_MAX}
          value={value}
          placeholder={DEFAULT_CUSTOMER_DISPLAY_GREETING}
          onChange={(e) => onChange(e.target.value)}
        />
      </Field>
      <div className="mt-1 flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Insert:</span>
          {GREETING_PLACEHOLDERS.map((token) => (
            <button
              key={token}
              type="button"
              onClick={() => insert(token)}
              className="rounded-md border border-border px-1.5 py-0.5 font-mono text-xs text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {token}
            </button>
          ))}
        </div>
        <span className="text-xs tabular-nums text-muted-foreground">
          {value.length}/{CUSTOMER_DISPLAY_GREETING_MAX}
        </span>
      </div>
      <div className="mt-3 rounded-md border border-border bg-muted/40 px-3 py-2.5 text-center">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Preview for a member named Jana
        </div>
        <div className="mt-1 text-base font-semibold text-foreground">{preview.title}</div>
        {preview.message && <div className="whitespace-pre-line text-sm text-foreground">{preview.message}</div>}
      </div>
    </SettingsCard>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-muted-foreground mb-1">{label}</span>
      {children}
    </label>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  /** Greys the switch out and refuses input — for a setting whose
   *  precondition is not met yet, where hiding the row entirely would leave
   *  an admin hunting for a control they were told exists. */
  disabled?: boolean;
}) {
  return (
    <div className={`flex items-start justify-between gap-4 ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}>
      <div>
        <div className="text-sm font-medium">{label}</div>
        {description && <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`group inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-all ${
          checked ? 'bg-primary border-primary justify-end pr-0.5' : 'bg-muted border-border justify-start pl-0.5'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 rounded-full shadow-sm transition-all ${
            checked ? 'bg-white' : 'bg-background'
          }`}
        />
      </button>
    </div>
  );
}

// Tri-state control for settings that can either inherit a device-local
// value (null) or be forced on/off from the dashboard (true/false).
function TriStateRow({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description?: string;
  value: boolean | null;
  onChange: (v: boolean | null) => void;
}) {
  const options: Array<{ label: string; val: boolean | null }> = [
    { label: 'Inherit POS setting', val: null },
    { label: 'Enabled', val: true },
    { label: 'Disabled', val: false },
  ];
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
      <div className="min-w-[15rem] flex-1">
        <div className="text-sm font-medium">{label}</div>
        {description && <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>}
      </div>
      <div className="inline-flex shrink-0 overflow-hidden rounded-md border border-border">
        {options.map((o) => {
          const active = value === o.val;
          return (
            <button
              key={String(o.val)}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(o.val)}
              className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                active ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ───────── Role Permissions ─────────

// `storeCredit` rows only mean something where a super admin has turned store
// credit on for the location, so they are shown only there. Their values are
// kept and saved either way.
const PERMISSION_COLUMNS: Array<{ key: keyof PosRolePermission; label: string; storeCredit?: true }> = [
  { key: 'canProcessOrders', label: 'Process Orders' },
  { key: 'canApplyDiscount', label: 'Apply Discount' },
  { key: 'canOverridePrice', label: 'Override Price' },
  { key: 'canVoidItem', label: 'Void Item' },
  { key: 'canProcessRefund', label: 'Process Refund' },
  { key: 'canApproveRefund', label: 'Approve Refund' },
  { key: 'canOpenDrawer', label: 'Open Drawer' },
  { key: 'canCashInOut', label: 'Cash In/Out' },
  { key: 'canViewTransactions', label: 'View Transactions' },
  { key: 'canVoidTransaction', label: 'Void Transaction' },
  { key: 'canViewReports', label: 'View Reports' },
  { key: 'canChangeSettings', label: 'Change Settings' },
  { key: 'canManageEmployees', label: 'Manage Employees' },
  { key: 'canAddProduct', label: 'Add Product' },
  { key: 'canUpdateApp', label: 'Update App' },
  { key: 'canIssueStoreCredit', label: 'Issue Store Credit', storeCredit: true },
  { key: 'canRedeemStoreCredit', label: 'Redeem Store Credit', storeCredit: true },
  { key: 'canShareStoreCreditLink', label: 'Share Store Credit Link', storeCredit: true },
  { key: 'canSellGiftCard', label: 'Sell Gift Card', storeCredit: true },
];

const DEFAULT_ROLES: Array<Pick<PosRolePermission, 'role'> & Partial<PosRolePermission>> = [
  {
    role: 'cashier',
    canProcessOrders: true,
    canOpenDrawer: true,
    canApplyDiscount: false,
    canOverridePrice: false,
    canVoidItem: false,
    canProcessRefund: false,
    canApproveRefund: false,
    canCashInOut: false,
    canViewTransactions: false,
    canVoidTransaction: false,
    canViewReports: false,
    canChangeSettings: false,
    canManageEmployees: false,
    canAddProduct: false,
    canUpdateApp: false,
    canIssueStoreCredit: false,
    canRedeemStoreCredit: true,
    canShareStoreCreditLink: true,
    canSellGiftCard: true,
  },
  {
    role: 'supervisor',
    canProcessOrders: true,
    canApplyDiscount: true,
    canVoidItem: true,
    canProcessRefund: true,
    canOpenDrawer: true,
    canCashInOut: true,
    canViewTransactions: true,
    canOverridePrice: false,
    canApproveRefund: false,
    canVoidTransaction: false,
    canViewReports: false,
    canChangeSettings: false,
    canManageEmployees: false,
    canAddProduct: false,
    canUpdateApp: false,
    canIssueStoreCredit: true,
    canRedeemStoreCredit: true,
    canShareStoreCreditLink: true,
    canSellGiftCard: true,
  },
  {
    role: 'manager',
    canProcessOrders: true,
    canApplyDiscount: true,
    canOverridePrice: true,
    canVoidItem: true,
    canProcessRefund: true,
    canApproveRefund: true,
    canOpenDrawer: true,
    canCashInOut: true,
    canViewTransactions: true,
    canVoidTransaction: true,
    canViewReports: true,
    canChangeSettings: false,
    canManageEmployees: false,
    canAddProduct: false,
    canUpdateApp: true,
    canIssueStoreCredit: true,
    canRedeemStoreCredit: true,
    canShareStoreCreditLink: true,
    canSellGiftCard: true,
  },
  {
    role: 'admin',
    canProcessOrders: true,
    canApplyDiscount: true,
    canOverridePrice: true,
    canVoidItem: true,
    canProcessRefund: true,
    canApproveRefund: true,
    canOpenDrawer: true,
    canCashInOut: true,
    canViewTransactions: true,
    canVoidTransaction: true,
    canViewReports: true,
    canChangeSettings: true,
    canManageEmployees: true,
    canAddProduct: true,
    canUpdateApp: true,
    canIssueStoreCredit: true,
    canRedeemStoreCredit: true,
    canShareStoreCreditLink: true,
    canSellGiftCard: true,
  },
];

function seedDefaults(locationId: string): PosRolePermission[] {
  const now = new Date().toISOString();
  return DEFAULT_ROLES.map((d) => ({
    id: crypto.randomUUID(),
    posPermissionId: `perm-${locationId}-${d.role}`,
    tenantId: '',
    locationId,
    role: d.role,
    canProcessOrders: d.canProcessOrders ?? true,
    canApplyDiscount: d.canApplyDiscount ?? false,
    canOverridePrice: d.canOverridePrice ?? false,
    canVoidItem: d.canVoidItem ?? false,
    canProcessRefund: d.canProcessRefund ?? false,
    canApproveRefund: d.canApproveRefund ?? false,
    canOpenDrawer: d.canOpenDrawer ?? true,
    canCashInOut: d.canCashInOut ?? false,
    canViewTransactions: d.canViewTransactions ?? false,
    canVoidTransaction: d.canVoidTransaction ?? false,
    canViewReports: d.canViewReports ?? false,
    canChangeSettings: d.canChangeSettings ?? false,
    canManageEmployees: d.canManageEmployees ?? false,
    canAddProduct: d.canAddProduct ?? false,
    canUpdateApp: d.canUpdateApp ?? false,
    canIssueStoreCredit: d.canIssueStoreCredit ?? false,
    canRedeemStoreCredit: d.canRedeemStoreCredit ?? true,
    canShareStoreCreditLink: d.canShareStoreCreditLink ?? true,
    canSellGiftCard: d.canSellGiftCard ?? true,
    syncedAt: now,
  }));
}

function RolePermissionsSection({ permsQuery }: { permsQuery: ReturnType<typeof usePosRolePermissions> }) {
  const { selectedLocationId } = useLocationStore();
  if (permsQuery.isLoading) return <Loading />;
  if (!selectedLocationId) return null;
  const data = permsQuery.data?.data ?? [];
  // Use latest syncedAt as the key: if any row's syncedAt changes (e.g.
  // after a successful save), the form remounts with fresh initial state.
  const latestSyncedAt = data.reduce((max, p) => (p.syncedAt > max ? p.syncedAt : max), '');
  const key = `${selectedLocationId}-${data.length > 0 ? latestSyncedAt : 'seeded'}`;
  return <RolePermissionsForm key={key} initial={data} locationId={selectedLocationId} />;
}

function RolePermissionsForm({ initial, locationId }: { initial: PosRolePermission[]; locationId: string }) {
  const update = useUpdatePosRolePermissions();
  const [seeded] = useState(initial.length === 0);
  const [local, setLocal] = useState<PosRolePermission[]>(() =>
    initial.length > 0 ? initial : seedDefaults(locationId),
  );
  const [saved, setSaved] = useState(false);
  // Per-location super-admin Store Credit gate (as in Store Settings). React
  // Query dedupes this with PosConfigPage's own call.
  const { data: storeCreditGate } = useProductsPosConfig(locationId);
  const storeCreditAllowed = storeCreditGate?.storeCreditEnabled === true;
  const columns = PERMISSION_COLUMNS.filter((col) => storeCreditAllowed || !col.storeCredit);

  const toggle = (permId: string, col: keyof PosRolePermission) => {
    setLocal((prev) => prev.map((p) => (p.id === permId ? { ...p, [col]: !p[col] } : p)));
    setSaved(false);
  };

  const handleSave = async () => {
    const result = (await update.mutateAsync({ locationId, permissions: local })) as {
      success: boolean;
      upserted: number;
      posPushOk?: boolean;
    };
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    if (result?.posPushOk === false) {
      toast.error(
        'Permissions saved to dashboard but failed to push to POS devices. POS devices may not reflect this change.',
      );
    }
  };

  return (
    <div className="space-y-3">
      {seeded && (
        <div className="flex gap-2 items-start rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs text-foreground">
          <div className="text-primary font-semibold shrink-0">ⓘ</div>
          <div>
            <span className="font-medium">No permissions synced yet</span> — showing default templates for 4 standard
            roles. Click <span className="font-semibold text-primary">Save & Push to POS</span> to commit these and push
            to POS devices.
          </div>
        </div>
      )}
      <div className="rounded-lg border border-border overflow-hidden overflow-x-auto bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-xs text-muted-foreground uppercase tracking-wide sticky left-0 bg-muted/40">
                Permission
              </th>
              {local.map((p) => (
                <th key={p.id} className="px-4 py-3 text-center font-semibold text-xs uppercase tracking-wide">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-primary/10 text-primary">
                    {p.role}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {columns.map((col, idx) => (
              <tr
                key={col.key}
                className={`border-t border-border transition-colors hover:bg-muted/30 ${
                  idx % 2 === 0 ? 'bg-background' : 'bg-muted/10'
                }`}
              >
                <td className="px-4 py-2.5 font-medium text-sm sticky left-0 bg-inherit">{col.label}</td>
                {local.map((p) => {
                  const has = p[col.key] as boolean;
                  return (
                    <td key={p.id} className="px-4 py-2.5 text-center">
                      <button
                        onClick={() => toggle(p.id, col.key)}
                        className={`group inline-flex h-9 w-16 items-center rounded-full border transition-all ${
                          has
                            ? 'bg-primary border-primary justify-end pr-1'
                            : 'bg-muted border-border justify-start pl-1'
                        }`}
                        aria-label={`Toggle ${col.label} for ${p.role}`}
                        aria-pressed={has}
                      >
                        <span
                          className={`inline-flex h-7 w-7 items-center justify-center rounded-full shadow-sm transition-all ${
                            has ? 'bg-white text-primary' : 'bg-background text-muted-foreground'
                          }`}
                        >
                          {has ? (
                            <Check className="h-4 w-4" strokeWidth={2.5} />
                          ) : (
                            <X className="h-4 w-4" strokeWidth={2} />
                          )}
                        </span>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between border-t border-border pt-3">
        <div className="text-xs text-muted-foreground">
          {initial.length > 0
            ? `Last synced: ${new Date(
                initial.reduce((max, p) => (p.syncedAt > max ? p.syncedAt : max), initial[0].syncedAt),
              ).toLocaleString()}`
            : 'Not yet synced'}
        </div>
        <div className="flex items-center gap-3">
          {saved && (
            <span className="text-xs text-green-600 flex items-center gap-1">
              <Check className="h-3.5 w-3.5" /> Saved — POS will update within 10s
            </span>
          )}
          {update.isError && <span className="text-xs text-red-600">Save failed</span>}
          <button
            onClick={handleSave}
            disabled={update.isPending}
            className="flex items-center gap-2 rounded bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" />
            {update.isPending ? 'Saving…' : 'Save & Push to POS'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────── Shared helpers ─────────

function StatCard({ label, value, tone }: { label: string; value: number; tone: 'neutral' | 'success' | 'warning' }) {
  const toneClass =
    tone === 'success'
      ? 'text-green-600 dark:text-green-400'
      : tone === 'warning'
        ? 'text-orange-600 dark:text-orange-400'
        : 'text-foreground';
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${toneClass}`}>{value}</div>
    </div>
  );
}

function Loading() {
  return <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>;
}
